/* Style Archive - fix layer.
 *
 * Part 1: video loading.
 * Problem: the grid videos used to ship with `autoplay preload="auto"`, so the browser
 * opened one full-file download per video to the CDN (capped at ~6 connections per host).
 * They starve each other and none of them ever buffers - every one sits at readyState 0
 * with 0 bytes. The old inline nudge() also called play() on display:none tiles, because
 * getBoundingClientRect() returns all zeros for them and that passed its in-view test, so
 * hidden videos competed for the same slots.
 *
 * Fix: park every grid video (src moved to data-src, preload="none", autoplay removed) so
 * nothing loads up front, then hand the src back only to videos actually scrolled into
 * view, capped at MAX concurrent. The hero video above the grid is deliberately left alone.
 *
 * 2026 fix: this used to scope itself to `document.querySelector('.ews')`, i.e. the FIRST
 * .ews wrapper on the page. The archive used to be split across two custom-liquid blobs
 * (`.ews` and `.ews ews2`) and every one of the grid's videos lived in the second one, so
 * none of them were ever parked. Now every `.ews` grid on the page is covered.
 *
 * Part 2 (2026-09-16): row-major reading order. See the block comment above rowMajor().
 */
(function () {
  var MAX = 4;

  function init() {
    var vids = [].slice.call(document.querySelectorAll('.ews .mt video'));
    if (!vids.length) return;

    var active = [];

    /* Park everything: no source, no preload, no autoplay. */
    vids.forEach(function (v) {
      v.removeAttribute('autoplay');
      v.preload = 'none';
      v.muted = true;
      v.playsInline = true;
      v.loop = true;
      try { v.pause(); } catch (e) {}
      var src = v.getAttribute('src') || v.getAttribute('data-src');
      if (src) {
        v.setAttribute('data-src', src);
        v.removeAttribute('src');
        try { v.load(); } catch (e) {}
      }
    });

    function hidden(v) {
      var tile = v.closest ? (v.closest('.mt') || v) : v;
      return !tile.offsetParent;
    }

    function play(v) {
      if (hidden(v) || active.indexOf(v) > -1) return;
      while (active.length >= MAX) {
        var o = active.shift();
        if (o !== v) { try { o.pause(); } catch (e) {} }
      }
      active.push(v);
      if (!v.getAttribute('src') && v.getAttribute('data-src')) {
        v.preload = 'auto';
        v.setAttribute('src', v.getAttribute('data-src'));
      }
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
    }

    function pause(v) {
      var i = active.indexOf(v);
      if (i > -1) active.splice(i, 1);
      if (!v.paused) { try { v.pause(); } catch (e) {} }
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) play(e.target); else pause(e.target);
        });
      }, { rootMargin: '250px 0px', threshold: 0.1 });
      vids.forEach(function (v) { io.observe(v); });
    } else {
      var t;
      var nudge = function () {
        var h = window.innerHeight || 0;
        vids.forEach(function (v) {
          if (hidden(v)) return;
          var r = v.getBoundingClientRect();
          if (!r.height) return;
          if (r.bottom > -h * 0.25 && r.top < h * 1.25) play(v); else pause(v);
        });
      };
      addEventListener('scroll', function () { clearTimeout(t); t = setTimeout(nudge, 120); }, { passive: true });
      addEventListener('resize', function () { clearTimeout(t); t = setTimeout(nudge, 200); }, { passive: true });
      nudge();
      setTimeout(nudge, 600);
    }

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) active.slice().forEach(pause);
    });

    ['touchstart', 'click'].forEach(function (evt) {
      document.addEventListener(evt, function () {
        active.forEach(function (v) {
          if (v.paused) { var p = v.play(); if (p && p.catch) p.catch(function () {}); }
        });
      }, { once: true, passive: true });
    });
  }

  /* ----------------------------------------------------------------------
   * Row-major reading order.
   *
   * The grid is a CSS multi-column (`columns:2` / `columns:3`) masonry, and a
   * multi-column box fills column one from top to bottom before it starts
   * column two. Tiles are emitted newest first, so a batch of new uploads all
   * landed in the left-hand column - the page read DOWN the first column
   * instead of ACROSS the first row.
   *
   * Fix: take the tiles out of the column box and deal them into explicit
   * column elements in DOM order. Tile 1 goes to column 1, tile 2 to column 2,
   * tile 3 to column 3, and from there each tile goes to whichever column is
   * currently shortest - so the newest content sits across the top row and the
   * archive reads left to right, row by row, all the way down, while the
   * columns still end up level (this is the standard masonry placement).
   *
   * Two layout passes only: deal round-robin so every tile is measured at its
   * final column width, take one batched read of every height, then re-deal
   * using those numbers. Media tiles carry an inline aspect-ratio, so their
   * height is known before the image loads and nothing reflows on load.
   *
   * Re-runs only when the column count actually changes (the 641px break).
   * ---------------------------------------------------------------------- */
  function rowMajor() {
    var grids = [].slice.call(document.querySelectorAll('.ews .grid'));
    if (!grids.length) return;

    function colCount(grid) {
      var n = parseInt(getComputedStyle(grid).getPropertyValue('--sa-cols'), 10);
      return (n && n > 0) ? n : 2;
    }

    function place(grid) {
      var n = colCount(grid);
      if (grid.__saCols === n) return;

      var tiles = grid.__saTiles;
      if (!tiles) {
        tiles = [].slice.call(grid.querySelectorAll('.mt'));
        if (!tiles.length) return;
        grid.__saTiles = tiles;
      }

      var gap = 16;
      try {
        var mb = parseFloat(getComputedStyle(tiles[0]).marginBottom);
        if (mb > 0) gap = mb;
      } catch (e) {}

      var colEls = [], frag = document.createDocumentFragment(), i, k;
      for (i = 0; i < n; i++) {
        var c = document.createElement('div');
        c.className = 'sa-col';
        frag.appendChild(c);
        colEls.push(c);
      }

      /* pass 1 - round robin, purely so each tile is laid out at final width */
      for (i = 0; i < tiles.length; i++) colEls[i % n].appendChild(tiles[i]);
      grid.textContent = '';
      grid.classList.add('sa-rows');
      grid.appendChild(frag);
      grid.__saCols = n;

      /* pass 2 - one batched read, then re-deal shortest-column in DOM order */
      var h = new Array(tiles.length);
      for (i = 0; i < tiles.length; i++) h[i] = tiles[i].offsetHeight || 0;

      var totals = new Array(n);
      for (i = 0; i < n; i++) { totals[i] = 0; colEls[i].textContent = ''; }
      for (i = 0; i < tiles.length; i++) {
        var min = 0;
        for (k = 1; k < n; k++) { if (totals[k] < totals[min]) min = k; }
        colEls[min].appendChild(tiles[i]);
        totals[min] += h[i] + gap;
      }
    }

    grids.forEach(place);

    var rt;
    addEventListener('resize', function () {
      clearTimeout(rt);
      rt = setTimeout(function () { grids.forEach(place); }, 180);
    }, { passive: true });
  }

  function boot() {
    setTimeout(function () {
      try { rowMajor(); } catch (e) {
        if (window.console && console.warn) console.warn('[sa-archive] row-major layout skipped', e);
      }
      init();
    }, 0);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();